import { log } from '@/utils/core/vueLogger';

export const iframeDataBridge = {
	_syncInterval: null,

	cachedEcosVM: null,
	lastEcosVMRef: null,
	cachedExternis: null,
	lastExternisRef: null,

	enumAppointment: {
		type: {
			HC: 1,
			CL: 2,
		},
		status: {
			SCHEDULED: 0,
			NONE: 1,
			CONFIRMED: 2,
			VALIDATED: 3,
			CANCELLED: 4,
			INPROGRESS: 5,
		},
		iconStatus: {
			PREPA: 'pause_circle',
			INPROGRESS: 'play_circle',
			SYNCERROR: 'icon-sync_problem',
			PENDINGSYNC: 'wifi',
		},
		getSubTypeId: function (label) {
			let subType = iframeDataBridge.getEcosVM()?.appointmentSubtypes.find((o) => o.label === label);
			return subType ? subType.id : 0;
		},
		getSubTypeIcon: function (label) {
			switch (label) {
				case 'PRIVATE':
					return 'lock';
				case 'NIGHT':
					return 'hotel';

				default:
					return 'storefront';
			}
		},
	},
	enumProduct: {
		statusRSWUserLine: {
			CANCELLED: 0,
			INCOMING: 1,
			RECIEVED: 2,
		},
		orderType: {
			REGULAR: 0,
			PREORDER: 1,
			RSW_RETRIEVE: 2,
			RSW_RETURN_CENTRAL_STOCK: 3,
			RSW_DESTRUCTION: 4,
			RSW_RETURN: 5,
			RSW_TRANSFER: 6,
			ASSORT_REGULAR: 7,
			ASSORT_RETURN: 8,
		},
	},

	getIframe() {
		const iframe = document.querySelector('iframe');
		return iframe || null;
	},

	getIframeWindow() {
		const iframe = document.querySelector('iframe');
		return iframe?.contentWindow || null;
	},

	getExternis(fromCache = true) {
		const win = this.getIframeWindow();
		const raw = win?.externis || null;

		// From cache
		if (this.lastExternisRef === raw && this.cachedExternis !== null && fromCache) {
			return this.cachedExternis;
		}

		// Update cache
		this.lastExternisRef = raw;
		this.cachedExternis = this.deepClone(raw);
		return this.cachedExternis;
	},

	getContext(fromCache = false) {
		if (fromCache) {
			const externis = this.getExternis(true);
			return externis?.ctx || null;
		} else {
			const win = this.getIframeWindow();
			return this.deepClone(win?.externis?.ctx || null);
		}
	},

	getCurrentUser() {
		const win = this.getIframeWindow();
		return this.deepClone(win?.externis?.ctx?.user || null);
	},

	getCustomer() {
		const win = this.getIframeWindow();
		return this.deepClone(win?.externis?.ctx?.customer?.reference || null);
	},

	getEcosVM(fromCache = true) {
		const win = this.getIframeWindow();
		const raw = win?.externis?.ecosVM || null;

		// From cache
		if (this.lastEcosVMRef === raw && this.cachedEcosVM !== null && fromCache) {
			return this.cachedEcosVM;
		}

		// Update cache
		this.lastEcosVMRef = raw;
		this.cachedEcosVM = this.deepClone(raw);
		return this.cachedEcosVM;
	},

	invalidateCachedEcosVM() {
		this.cachedEcosVM = null;
		this.lastEcosVMRef = null;
	},

	invalidateCachedExternis() {
		this.cachedExternis = null;
		this.lastExternisRef = null;
	},

	getCurrentView() {
		return this.getEcosVM()?.currentView?.() || null;
	},

	getStorage() {
		return this.getEcosVM()?.storage || null;
	},

	getStorageAsync(key) {
		return new Promise((resolve) => {
			this.getStorage().get(key, resolve);
		});
	},

	getService() {
		return this.getEcosVM()?.svc || null;
	},

	getAppointmentTranslator() {
		return this.getEcosVM()?.appointmentTranslator || null;
	},

	getDeliveryLocationTranslator() {
		return this.getEcosVM()?.deliveryLocationTranslator || null;
	},

	getDateHelper() {
		const win = this.getIframeWindow();
		return win?.dateHelper || null;
	},

	getSfaHelper() {
		const win = this.getIframeWindow();
		return win?.externis?.sales?.sfaHelper || null;
	},

	getSalesHelper() {
		const win = this.getIframeWindow();
		return win?.externis?.sales?.salesHelper || null;
	},

	getProducts() {
		const win = this.getIframeWindow();
		const ecosVM = win?.externis?.ecosVM;
		if (!ecosVM) return [];
		const salesProducts = typeof ecosVM.products === 'function' ? (ecosVM.products() || []) : [];
		const assortProducts = Array.isArray(ecosVM.productsAssort) ? ecosVM.productsAssort : [];
		// Assort first so sales entries win on duplicate id (sales carries the reference field)
		return [...assortProducts, ...salesProducts];
	},

	isSyncEnded(objectNames, callback = null) {
		const win = this.getIframeWindow();
		const synchProcess = win?.externis?.ecosVM?.synchProcess || {};
		let allSynchEnded = true;

		objectNames.forEach((objectName) => {
			if (!synchProcess[objectName]) {
				allSynchEnded = false;
			}
		});

		if (!allSynchEnded && callback) {
			if (this._syncInterval) return allSynchEnded;

			let attempts = 0;
			this._syncInterval = setInterval(() => {
				if (attempts >= 60) log('error', 'iframeDataBridge', 'Sync attempts exceeded 60, stopping interval.', objectNames);

				if (this.isSyncEnded(objectNames)) log('info', 'iframeDataBridge', 'Sync ended for all objects:', objectNames);

				if (attempts >= 60 || this.isSyncEnded(objectNames)) {
					clearInterval(this._syncInterval);
					this._syncInterval = null;
					callback();
				}
				attempts++;
			}, 1000);
		}

		return allSynchEnded;
	},

	isOffline() {
		const win = this.getIframeWindow();
		return !win?.externis?.ctx?.isOnline() || false;
	},

	isOnline() {
		const win = this.getIframeWindow();
		return win?.externis?.ctx?.isOnline() || false;
	},

	isPlainObject(obj) {
		return Object.prototype.toString.call(obj) === '[object Object]';
	},

	deepClone(obj, seen = new WeakMap()) {
		if (obj === null || typeof obj !== 'object') return obj;

		// prevent circular references
		if (seen.has(obj)) return seen.get(obj);

		// skip Vue component instances or DOM nodes
		if (
			obj instanceof Element || // DOM element
			obj instanceof Node || // any DOM node
			obj._isVue ||
			obj.__v_skip ||
			obj.__v_isRef || // Vue 3 internals
			typeof obj.render === 'function' // Vue component-like
		) {
			return undefined;
		}

		// handle date : iframe dates come from another realm, so instanceof Date is unreliable here
		if (Object.prototype.toString.call(obj) === '[object Date]') return new Date(obj.getTime());

		// handle array
		if (Array.isArray(obj)) {
			const arr = [];
			seen.set(obj, arr);
			for (let i = 0; i < obj.length; i++) {
				arr[i] = this.deepClone(obj[i], seen);
			}
			return arr;
		}

		// skip non-plain objects (e.g. class instances)
		if (!this.isPlainObject(obj)) return undefined;

		// handle object
		const clone = {};
		seen.set(obj, clone);
		for (const key in obj) {
			if (Object.prototype.hasOwnProperty.call(obj, key)) {
				clone[key] = this.deepClone(obj[key], seen);
			}
		}
		return clone;
	},

	/**
	 * Send SSO authentication data to iframe
	 * @param {Object} ssoData - SSO authentication data
	 */
	sendSSOAuthToIframe(ssoData) {
		const iframe = this.getIframe();
		if (iframe && iframe.contentWindow) {
			const message = {
				type: 'SSO_AUTH',
				data: ssoData,
				timestamp: Date.now(),
			};

			try {
				iframe.contentWindow.postMessage(message, window.location.origin);
				log('iframeDataBridge: Sent SSO auth data to iframe', message);
			} catch (error) {
				console.error('iframeDataBridge: Failed to send SSO auth data', error);
			}
		}
	},

	/**
	 * Initialize SSO message listener
	 * Call this when the iframe loads to listen for SSO responses
	 */
	initSSOListener() {
		window.addEventListener('message', (event) => {
			if (event.origin !== window.location.origin) return;

			if (event.data && event.data.type === 'SSO_AUTH_RESPONSE') {
				log('iframeDataBridge: Received SSO auth response from iframe', event.data);
				// Handle SSO authentication response
				this.handleSSOResponse(event.data);
			}
		});
	},

	/**
	 * Handle SSO response from iframe
	 * @param {Object} responseData - Response data from iframe
	 */
	handleSSOResponse(responseData) {
		const { success, error, redirectUrl } = responseData.data || {};

		if (success) {
			log('iframeDataBridge: SSO authentication successful');
			// SSO authentication was successful
			if (redirectUrl) {
				window.location.href = redirectUrl;
			}
		} else {
			console.error('iframeDataBridge: SSO authentication failed', error);
			// Handle SSO failure
		}
	},
};
